import axios from "axios";
import { auth } from "./firebase";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Admin ────────────────────────────────────────────
export const getPublicStats = () => api.get("/admin/public-stats");
export const getAdminStats = () => api.get("/admin/stats");

// ─── Courses ──────────────────────────────────────────
export const getCourses = (params) => api.get("/courses", { params });
export const getCourse = (id) => api.get(`/courses/${id}`);
export const createCourse = (data) => api.post("/courses", data);
export const updateCourse = (id, d) => api.put(`/courses/${id}`, d);
export const getMyCourses = (uid) =>
  api.get("/courses", { params: { teacherId: uid } });
export const getCourseReviews = (id) => api.get(`/courses/${id}/reviews`);
export const addReview = (id, d) => api.post(`/courses/${id}/reviews`, d);

// ─── Users ────────────────────────────────────────────
export const registerUser = (data) => api.post("/users/register", data);
export const getMe = () => api.get("/users/me");
export const updateMe = (data) => api.put("/users/me", data);
export const getTeachers = (params) => api.get("/users/teachers", { params });
export const getTeacher = (id) => api.get(`/users/teachers/${id}`);

// ─── Enrollments ──────────────────────────────────────
export const getMyEnrollments = () => api.get("/enrollments/my");
export const getTeacherEnrollments = () => api.get("/enrollments/teacher");
export const updateProgress = (enrollmentId, lessonId) =>
  api.post(`/enrollments/${enrollmentId}/progress`, { lessonId });

// ─── Payments ─────────────────────────────────────────
// couponCode is now an optional 4th argument
export const createCheckout = (
  courseId,
  method = "card",
  phoneNumber,
  couponCode
) =>
  api.post("/payments/checkout", { courseId, method, phoneNumber, couponCode });
export const getEarnings = () => api.get("/payments/earnings");

// ─── Subjects ─────────────────────────────────────────
export const getSubjects = () => api.get("/subjects");
export const addSubject = (subject) => api.post("/subjects", { subject });

// ─── Upload ───────────────────────────────────────────
export const uploadVideo = (formData, onProgress) =>
  api.post("/upload/video", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) =>
      onProgress && onProgress(Math.round((e.loaded * 100) / e.total)),
  });
export const uploadThumbnail = (formData) =>
  api.post("/upload/thumbnail", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const uploadAvatar = (fd) =>
  api.post("/upload/avatar", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const uploadBanner = (fd) =>
  api.post("/upload/banner", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const uploadSubjectImage = (fd) =>
  api.post("/upload/subject-image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const uploadCourseBannerFile = (fd) =>
  api.post("/upload/course-banner", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const uploadCampaignBannerFile = (fd) =>
  api.post("/upload/campaign-banner", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const getSignedVideoUrl = (publicId) =>
  api.get("/upload/video-url", { params: { id: publicId } });
export const getAdminUsers = (params) => api.get("/admin/users", { params });
export const getPendingTeachers = () => api.get("/admin/teachers/pending");
export const approveTeacher = (id, approved) =>
  api.put(`/admin/teachers/${id}/approve`, { approved });
export const banUser = (id, banned) =>
  api.put(`/admin/users/${id}/ban`, { banned });
export const deleteUser = (id) => api.delete(`/admin/users/${id}`);
export const getAdminCourses = (params) =>
  api.get("/admin/courses", { params });
export const approveCourse = (id, approved) =>
  api.put(`/admin/courses/${id}/approve`, { approved });
export const featureCourse = (id, featured) =>
  api.put(`/admin/courses/${id}/feature`, { featured });
export const deleteAdminCourse = (id) => api.delete(`/admin/courses/${id}`);
export const getAdminTransactions = () => api.get("/admin/transactions");

// ─── PDFs ─────────────────────────────────────────────
export const uploadPdf = (fd, onProgress) =>
  api.post("/pdfs/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) =>
      onProgress && onProgress(Math.round((e.loaded * 100) / e.total)),
  });
export const getCoursePdfs = (courseId, lessonIndex) =>
  api.get(
    `/pdfs/${courseId}`,
    lessonIndex !== undefined ? { params: { lessonIndex } } : {}
  );
export const deletePdf = (id) => api.delete(`/pdfs/${id}`);

// ─── Reels ────────────────────────────────────────────
export const getReels = (params) => api.get("/reels", { params });
export const uploadReel = (fd, onProgress) =>
  api.post("/reels", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) =>
      onProgress && onProgress(Math.round((e.loaded * 100) / e.total)),
  });
export const likeReel = (id) => api.post(`/reels/${id}/like`);
export const saveReel = (id) => api.post(`/reels/${id}/save`);
export const viewReel = (id) => api.post(`/reels/${id}/view`);
export const deleteReel = (id) => api.delete(`/reels/${id}`);

// ─── Coupons (NEW) ────────────────────────────────────
export const validateCoupon = (code, courseId, price) =>
  api.post("/coupons/validate", { code, courseId, price });
export const createCoupon = (data) => api.post("/coupons", data);
export const getCoupons = () => api.get("/coupons");
export const updateCoupon = (id, d) => api.patch(`/coupons/${id}`, d);
export const deleteCoupon = (id) => api.delete(`/coupons/${id}`);

// ─── Campaigns (NEW) ──────────────────────────────────
export const getActiveCampaigns = () => api.get("/campaigns");
export const getAllCampaigns = () => api.get("/campaigns/all");
export const createCampaign = (data) => api.post("/campaigns", data);
export const updateCampaign = (id, d) => api.patch(`/campaigns/${id}`, d);
export const deleteCampaign = (id) => api.delete(`/campaigns/${id}`);

// ─── Quizzes (NEW) ────────────────────────────────────
export const createQuiz = (data) => api.post("/quizzes", data);
export const updateQuiz = (id, data) => api.put(`/quizzes/${id}`, data);
export const getQuiz = (id) => api.get(`/quizzes/${id}`);
export const getLessonQuiz = (courseId, idx) =>
  api.get(`/quizzes/lesson/${courseId}/${idx}`);
export const submitQuiz = (id, answers) =>
  api.post(`/quizzes/${id}/submit`, { answers });
export const getQuizResults = (id) => api.get(`/quizzes/${id}/results`);

// ─── Announcements (NEW) ──────────────────────────────
export const getAnnouncements = (courseId) => api.get(`/announcements/${courseId}`);
export const getMyAnnouncements = () => api.get("/announcements/my/recent");
export const createAnnouncement = (data) => api.post("/announcements", data);
export const deleteAnnouncement = (id) => api.delete(`/announcements/${id}`);

// ─── Wishlist (NEW) ───────────────────────────────────
export const toggleWishlist = (courseId) => api.post(`/users/wishlist/${courseId}`);
export const toggleFavoriteTeacher = (teacherId) => api.post(`/users/favorite-teacher/${teacherId}`);

// ─── Analytics (NEW) ──────────────────────────────────
export const getAdminAnalytics = () => api.get("/analytics/admin");
export const getTeacherAnalytics = () => api.get("/analytics/teacher");
export const getStudentAnalytics = () => api.get("/analytics/student");

// ─── Messages ─────────────────────────────────────────
export const submitInquiry = (data) => api.post("/messages", data);
export const getInquiries = () => api.get("/messages");
export const updateInquiryStatus = (id, status) => api.patch(`/messages/${id}`, { status });
export const deleteInquiry = (id) => api.delete(`/messages/${id}`);

export default api;
